import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { GlobalLoader, Tooltip } from '@wf/sw-ui-kit';
import { Button, Modal, Table } from '@wf/components';

import EmptyTableContent from '../../../../components/EmptyTableContent';

import { type IFeesToBePaidResponse } from '../../types/response';

import { defaultScaleSelector } from '@/store/configs/selectors';
import formatNumber from '../../../../helpers/formatNumber';
import { getFeesToBePaid } from '../../services/lpco';

import styles from './FeePopup.module.scss';

interface IFeePopupProps {
  onClose: () => void;
}

function FeePopup({ onClose }: IFeePopupProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { id: lpcoId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const defaultScale = useSelector(defaultScaleSelector);

  const [{ fees, amountToBePaid }, setFeesToBePaid] = useState<IFeesToBePaidResponse>({
    amountToBePaid: 0,
    fees: [],
  });

  useEffect(() => {
    async function getFees() {
      try {
        const { data } = await getFeesToBePaid(lpcoId);
        setFeesToBePaid({ ...data, fees: data.fees.reverse() });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }

    void getFees();
  }, [lpcoId]);

  if (loading) {
    return <GlobalLoader className={styles.loader} />;
  }

  return (
    <Modal handleClose={onClose} id="item-usage-modal" className={styles.modal} isOpen>
      <Modal.Header className={styles.modalHeader}>
        <Modal.Title>{t('Fees')}</Modal.Title>
      </Modal.Header>
      <Modal.Content>
        <Table>
          <Table.Grid>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className={styles.firstCell}>{t('#')}</Table.HeaderCell>
                <Table.HeaderCell>
                  <Tooltip tooltipContent={t('The unique code assigned to the fee')}>{t('Code')}</Tooltip>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <Tooltip tooltipContent={t('A brief description of the fee')}>{t('Description')}</Tooltip>
                </Table.HeaderCell>
                <Table.HeaderCell align="right">
                  <Tooltip tooltipContent={t('The initial amount before any additional charges or taxes are applied')}>
                    {t('Base Amount')}
                  </Tooltip>
                </Table.HeaderCell>
                <Table.HeaderCell align="right">
                  <Tooltip
                    tooltipContent={t('The percentage or rate applied to the base amount to calculate the total fee')}
                  >
                    {t('Rate')}
                  </Tooltip>
                </Table.HeaderCell>
                <Table.HeaderCell align="right">
                  <Tooltip
                    tooltipContent={t('The total amount of the fee, calculated based on the base amount and rate')}
                  >
                    {t('Amount')}
                  </Tooltip>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body maxRows={10}>
              {fees.length ? (
                <>
                  {fees.map(({ id, feeCode, feeDescription, rate, amount, baseAmount }, index: number) => (
                    <Table.Row key={id}>
                      <Table.Cell className={styles.firstCell}>{index + 1}</Table.Cell>
                      <Table.Cell>{feeCode}</Table.Cell>
                      <Table.Cell>{feeDescription}</Table.Cell>
                      <Table.Cell align="right">{formatNumber({ value: baseAmount, locale: language })}</Table.Cell>
                      <Table.Cell align="right">
                        {formatNumber({
                          value: rate,
                          locale: language,
                          maximumFractionDigits: 7,
                          minimumFractionDigits: defaultScale,
                        })}
                      </Table.Cell>
                      <Table.Cell align="right">{formatNumber({ value: amount, locale: language })}</Table.Cell>
                    </Table.Row>
                  ))}
                  <Table.Row className={styles.totalAmountRow}>
                    <div>{t('Total Amount To Be Paid')}</div>
                    <span className={styles.totalAmount}>
                      {formatNumber({ value: amountToBePaid, locale: language })}
                    </span>
                  </Table.Row>
                </>
              ) : (
                <div className={styles.emptyMessage}>
                  <EmptyTableContent title={t('No fee data available')} />
                </div>
              )}
            </Table.Body>
          </Table.Grid>
        </Table>
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={onClose} color="typography" secondary>
          {t('Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default FeePopup;
